/**
 * The portal's 404 — what an unknown path reaches (#17).
 *
 * TanStack's built-in fallback is `<p>Not Found</p>`: untranslated on a portal that is bilingual by
 * rule, unstyled, and rendered outside `AppShell`. Caddy serves the SPA with
 * `try_files … /index.html`, so in production a mistyped deep link or a stale bookmark lands
 * here rather than on a server error page — which makes this a screen users actually see.
 *
 * `EmptyState`, not `ErrorState`: a wrong URL is not a server failure, and `ErrorState` carries
 * `role="alert"` and the red icon, which overstate it and would announce an incident to a screen
 * reader that is merely lost.
 *
 * ⚠️ **This renders at the router level, above `AuthenticatedLayout`, so it does not inherit the
 * auth gate** — the three states have to be handled here or they arrive by accident:
 *
 *  - `restoring` — the same progress bar the authenticated layout shows. Without it a cold reload
 *    on an unknown path flashes the 404 before the silent refresh has decided anything.
 *  - `anonymous` — `/login`. Rendering the shell with an empty nav for somebody who is not signed
 *    in would be worse than the framework default it replaces.
 *  - signed in — the shell, so they keep the navigation and can leave without the back button.
 */

import { Navigate, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { landingPathFor } from '../deca/navigation.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, EmptyState, ProgressBar } from '../ui/primitives.tsx'

export function NotFoundScreen() {
  const { t } = useTranslation()
  const { status, principal } = useAuth()
  const navigate = useNavigate()

  if (status === 'restoring') {
    return (
      <div className="auth-screen">
        <div style={{ width: 240 }}>
          <ProgressBar />
        </div>
      </div>
    )
  }

  if (status === 'anonymous') return <Navigate to="/login" />

  // Where this role's portal begins, not a hardcoded `/`: a platform role is redirected straight
  // back out of the Panel, so "go home" has to mean the same thing here as everywhere else.
  const home = landingPathFor(principal)

  return (
    <AppShell title={t('notFound.title')}>
      <Card>
        <EmptyState
          icon="search"
          title={t('notFound.title')}
          body={t('notFound.body')}
          action={
            <Button variant="primary" onClick={() => void navigate({ to: home })}>
              {t('notFound.action')}
            </Button>
          }
        />
      </Card>
    </AppShell>
  )
}
